import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { PluginKey } from '@tiptap/pm/state';
import type { SupabaseClient } from '../../lib/supabase';
import { createDm } from '../messages/rooms';
import { buildSuggestion } from '../notes/mentionSuggestion';
import type { SuggestionRow } from '../notes/mentionSuggestion';
import { likeTerm, searchMentions } from '../notes/noteMentions';
import type { MentionItem, MentionSources } from '../notes/noteMentions';
import { todayIn } from '../shift/shiftWindow';

const PER_KIND = 5;
const KILLUA_EMAIL = 'killua.agent@4ocean.com';

export interface CommandMenuPerson {
  id: string;
  label: string;
  email?: string;
  /** Present only when the person is in fetchChatPeople's eligible list. */
  mayStartDm?: boolean;
}

type CommandName = 'find' | 'touch' | 'deal' | 'lead' | 'killua';

export interface CommandMenuRow extends SuggestionRow {
  command: CommandName;
  href?: string;
  action: 'command' | 'link' | 'killua';
  killuaId?: string;
}

const COMMANDS: Array<{ command: Exclude<CommandName, 'killua'>; hint: string }> = [
  { command: 'find', hint: 'Find a project, task, deal, lead or CG order' },
  { command: 'touch', hint: 'Find a lead by touch history' },
  { command: 'deal', hint: 'Find a deal by name' },
  { command: 'lead', hint: 'Find a lead by name or company' },
];

function commandRow(command: CommandName, hint: string): CommandMenuRow {
  return { id: `command-${command}`, label: `/${command}`, hint, command, action: 'command' };
}

/** The command list is deliberately ranked, not alphabetical. */
export function commandMenuRows(query: string, people: CommandMenuPerson[]): CommandMenuRow[] {
  const needle = query.trim().toLowerCase();
  const commands: CommandMenuRow[] = COMMANDS.map((item) => commandRow(item.command, item.hint));
  const killua = people.find(
    (person) => person.mayStartDm === true && person.email?.toLowerCase() === KILLUA_EMAIL,
  );
  if (killua !== undefined) {
    commands.push({
      id: `command-killua-${killua.id}`,
      label: '/killua',
      hint: 'Open your private chat with Killua',
      command: 'killua',
      action: 'killua',
      killuaId: killua.id,
    });
  }
  return needle === '' ? commands : commands.filter((item) => item.command.startsWith(needle));
}

function recordHref(item: MentionItem): string {
  if (item.kind === 'order') return `/cg-orders/${item.id}`;
  if (item.kind === 'task') return `${item.href}?task=${encodeURIComponent(item.id)}`;
  return item.href;
}

function recordRows(items: MentionItem[], command: CommandName = 'find'): CommandMenuRow[] {
  return items.map((item) => ({
    id: item.id,
    label: item.label,
    hint: item.hint,
    command,
    href: recordHref(item),
    action: 'link',
  }));
}

function leadName(row: { first_name: string; last_name: string; company_name: string }): string {
  return `${row.first_name} ${row.last_name}`.trim() || row.company_name;
}

async function searchLeads(
  client: SupabaseClient,
  query: string,
  touch: boolean,
): Promise<CommandMenuRow[]> {
  const term = likeTerm(query);
  const columns = touch
    ? 'id, first_name, last_name, company_name, touch_count, last_touch_at, lead_stages:stage_id (name)'
    : 'id, first_name, last_name, company_name, lead_stages:stage_id (name)';
  const { data } = await client
    .from('leads')
    .select(columns)
    .or(`first_name.ilike.${term},last_name.ilike.${term},company_name.ilike.${term}`)
    .limit(PER_KIND);
  return (data ?? []).map((row) => {
    const lead = row as unknown as {
      id: string;
      first_name: string;
      last_name: string;
      company_name: string;
      touch_count?: number;
      last_touch_at?: string | null;
      lead_stages: { name: string } | null;
    };
    const base = `${lead.company_name === '' ? 'No company' : lead.company_name} · ${lead.lead_stages?.name ?? 'No stage'}`;
    const lastTouch =
      lead.last_touch_at === undefined || lead.last_touch_at === null
        ? 'No touches'
        : `Last touch: ${todayIn('Asia/Manila', new Date(lead.last_touch_at))}`;
    return {
      id: lead.id,
      label: leadName(lead),
      hint: touch ? `${base} · ${String(lead.touch_count ?? 0)} touches · ${lastTouch}` : base,
      command: touch ? 'touch' : 'lead',
      href: `/tracker?lead=${lead.id}`,
      action: 'link' as const,
    };
  });
}

async function searchDeals(client: SupabaseClient, query: string): Promise<CommandMenuRow[]> {
  const term = likeTerm(query);
  const { data } = await client
    .from('deals')
    .select(
      'id, name, next_action_date, deal_stages:stage_id (name), profiles:owner_id (display_name)',
    )
    .ilike('name', term)
    .limit(PER_KIND);
  return (data ?? []).map((deal) => {
    const owner = deal.profiles?.display_name.trim() || 'Unassigned';
    return {
      id: deal.id,
      label: deal.name,
      hint: `${deal.deal_stages?.name ?? 'No stage'} · ${deal.next_action_date ?? 'No next action'} · ${owner}`,
      command: 'deal',
      href: `/deals?deal=${deal.id}`,
      action: 'link' as const,
    };
  });
}

/**
 * Results after the command and its search text. The standard search remains
 * untouched for /find; richer lead/deal reads are only made for their command.
 */
export async function searchCommandRows(
  client: SupabaseClient,
  query: string,
  sources: MentionSources,
  people: CommandMenuPerson[] = [],
): Promise<CommandMenuRow[]> {
  const trimmed = query.trim();
  const [rawCommand = '', ...terms] = trimmed.split(/\s+/);
  const command = rawCommand.toLowerCase() as CommandName;
  const term = terms.join(' ');
  if (!['find', 'touch', 'deal', 'lead', 'killua'].includes(command) || term === '') {
    return commandMenuRows(trimmed, people);
  }
  if (command === 'find') return recordRows(await searchMentions(client, term, sources));
  if (command === 'lead') {
    return sources.salesMember === true ? searchLeads(client, term, false) : [];
  }
  if (command === 'touch') {
    return sources.salesMember === true ? searchLeads(client, term, true) : [];
  }
  if (command === 'deal') {
    return sources.dealAccess === true ? searchDeals(client, term) : [];
  }
  return [];
}

export async function openKilluaChat(
  client: SupabaseClient,
  killuaId: string,
  navigate: (href: string) => void | Promise<void>,
): Promise<void> {
  const roomId = await createDm(client, killuaId);
  void Promise.resolve(navigate(`/messages?room=${encodeURIComponent(roomId)}`)).catch(
    (cause: unknown) => console.error('Could not open Killua chat', cause),
  );
}

function attrsFor(row: CommandMenuRow): Record<string, unknown> {
  return { ...row };
}

const COMMAND_MENU_PLUGIN_KEY = new PluginKey('commandMenuSuggestion');

/** Kept separate so the start-of-line contract has a focused behavior test. */
export const slashMenuOptions = { char: '/', allowSpaces: true, startOfLine: true } as const;

const COMMAND_NAMES: readonly string[] = ['find', 'touch', 'deal', 'lead', 'killua'];

/**
 * Whether the text after "/" could still be a command. The menu stays shut
 * otherwise, so a message that merely starts with "/" ("/projects/abc is
 * broken") still sends on Enter.
 */
export function mayBeCommand(query: string): boolean {
  const [word = '', ...rest] = query.split(/\s/);
  const name = word.toLowerCase();
  return rest.length === 0
    ? COMMAND_NAMES.some((command) => command.startsWith(name))
    : COMMAND_NAMES.includes(name);
}

export function CommandMenuSuggestion(config: {
  client: SupabaseClient;
  sources: () => MentionSources;
  people: () => CommandMenuPerson[];
  navigate: () => ((href: string) => void | Promise<void>) | undefined;
}) {
  const popover = buildSuggestion<CommandMenuRow>({
    search: (query) => searchCommandRows(config.client, query, config.sources(), config.people()),
    attrs: attrsFor,
    ariaLabel: 'Message command menu',
    minChars: 0,
  });
  return Extension.create({
    name: 'commandMenuSuggestion',
    // Above submitKeymap (default 100), like the @ Mention node (101), so
    // Enter picks the highlighted row instead of sending "/deal acme".
    priority: 101,
    addProseMirrorPlugins() {
      return [
        Suggestion({
          editor: this.editor,
          pluginKey: COMMAND_MENU_PLUGIN_KEY,
          ...popover,
          ...slashMenuOptions,
          allow: ({ state, range }) =>
            mayBeCommand(state.doc.textBetween(range.from + 1, range.to, ' ')),
          command: ({ editor, range, props }) => {
            const row = props as unknown as CommandMenuRow;
            if (row.action === 'killua' && row.killuaId !== undefined) {
              editor.chain().focus().deleteRange(range).run();
              const navigate = config.navigate();
              if (navigate !== undefined) {
                void openKilluaChat(config.client, row.killuaId, navigate).catch((cause: unknown) =>
                  console.error('Could not start Killua chat', cause),
                );
              }
              return;
            }
            if (row.action === 'command') {
              editor.chain().focus().insertContentAt(range, `/${row.command} `).run();
              return;
            }
            if (row.href === undefined) return;
            editor
              .chain()
              .focus()
              .insertContentAt(range, [
                {
                  type: 'text',
                  marks: [{ type: 'link', attrs: { href: row.href } }],
                  text: row.label,
                },
                { type: 'text', text: ' ' },
              ])
              .run();
          },
        }),
      ];
    },
  });
}
